// External party portals — Broker "Commissions" (PR-C) reads. Party-scoped,
// read-only projection over the income ledger: the broker's recognized
// commission payouts (the waterfall broker carve) across the loans they
// brokered. The portal READS recognized commission; it does not compute it.

import { portalApi } from "./client";

export interface BrokerCommissionLoanRow {
  loanId: string;
  loanNumber: string;
  totalEarned: number;
  paymentCount: number;
  lastPaidAt: string | null;
}

export interface BrokerCommissionEvent {
  recognizedAt: string;
  periodStart: string | null;
  amount: number;
  typeLabel: string;
}

export interface BrokerFinanceSummary {
  totalEarned: number;
  loanCount: number;
  paymentCount: number;
  lastPaidAt: string | null;
  loans: BrokerCommissionLoanRow[];
  // Audit F-12 — recognized commission paid in the current calendar year
  // (server-computed on RecognizedAt). Optional/nullable so older payloads
  // and fixtures stay compatible; the UI renders "—" when absent.
  paidYearToDate?: number | null;
  // Brokerage / Sub-Broker gross-only visibility — true when the covering firm
  // has hidden a junior broker's commission figures. When set, everything else
  // is zeroed/empty and the tab shows a "hidden by your firm" state.
  financialsHidden?: boolean;
}

export interface BrokerLoanCommissionDetail {
  loanId: string;
  loanNumber: string;
  totalEarned: number;
  paymentCount: number;
  lastPaidAt: string | null;
  events: BrokerCommissionEvent[];
}

export const portalBrokerFinanceApi = {
  /** Cross-loan commission roll-up for the authenticated broker. */
  summary: () => portalApi.get<BrokerFinanceSummary>("/broker/finance/summary"),
  /** One brokered loan's commission history (404 when not this broker's loan). */
  loan: (loanId: string) =>
    portalApi.get<BrokerLoanCommissionDetail>(`/broker/finance/loans/${loanId}`),
  /** On-demand monthly commission-statement PDF blob (period = YYYY-MM). */
  statement: (period: string) =>
    portalApi.getBlob("/broker/finance/statement", { period }),
};
